/* eslint-disable complexity */
/* eslint-disable max-statements */
/* eslint-disable max-lines */
import { useCallback, useContext, useMemo } from 'react';
import { ControlledDateTimePickerContext } from '../../ControlledDateTimePickerCTX.js';
import type { DSControlledDateTimePickerT } from '../../react-desc-prop-types.js';
import type { DSControlledDateTimePickerInternalsT } from '../../sharedTypes.js';
import { defaultMetaInfo } from '../../utils/constants.js';
import { getValidationDateStringMetaInfo } from '../../utils/dateHelpers.js';
import {
  fillStringWithPlaceHolders,
  getFormattedDateString,
  prependStringWithPlaceHolders,
} from '../../utils/stringHelpers.js';

export const useDateInputs = (): DSControlledDateTimePickerInternalsT.UseDateInputsR => {
  const {
    props: { disabled, applyAriaDisabled, readOnly },
    appOnMonthChange,
    appOnDayChange,
    appOnYearChange,
    getIsDisabledDay,
    getIsOutOfRangeDay,
    month,
    day,
    year,
    hideTime,
    handleChangeComposedDateString,
    withClearBtn,
    withAnyPicker,
    withAnyRightController,
    autoFocusMonthInput,
    innerRefMonthInput,
    trackFocusResetter,
    trackFocusFirstSegment,
    trackFocusLastSegment,
    trackFocusDayInput,
    trackFocusMonthInput,
    trackFocusYearInput,
    trackFocusHourInput,
    trackFocusClearBtn,
    trackFocusPicker,
  } = useContext(ControlledDateTimePickerContext);

  const shouldPreventValueChanges = disabled || applyAriaDisabled || readOnly;

  const onHomeKeyDown = useCallback(() => {
    trackFocusFirstSegment();
  }, [trackFocusFirstSegment]);
  const onEndKeyDown = useCallback(() => {
    trackFocusLastSegment();
  }, [trackFocusLastSegment]);

  const handleChangeMonth = useCallback(
    (
      newMonth: string,
      e: DSControlledDateTimePickerT.OnInternalValuesChangeEvent,
      metaInfo: Partial<DSControlledDateTimePickerT.InternalInputsChangeMetaInfo> = defaultMetaInfo,
    ) => {
      if (shouldPreventValueChanges) return;
      const newDateString = getFormattedDateString({
        month: newMonth,
        day,
        year,
      });
      const finalMetaInfo: DSControlledDateTimePickerT.InternalInputsChangeMetaInfo = {
        ...metaInfo,
        ...getValidationDateStringMetaInfo(newDateString, getIsOutOfRangeDay, getIsDisabledDay),
        newDateString,
      };
      appOnMonthChange(newMonth, e, finalMetaInfo);
      handleChangeComposedDateString(newDateString, finalMetaInfo);
    },
    [
      shouldPreventValueChanges,
      day,
      year,
      getIsOutOfRangeDay,
      getIsDisabledDay,
      appOnMonthChange,
      handleChangeComposedDateString,
    ],
  );
  const onMonthFocus: DSControlledDateTimePickerInternalsT.OnFocusFunc = useCallback(() => {
    trackFocusMonthInput();
  }, [trackFocusMonthInput]);
  const onMonthChange: DSControlledDateTimePickerInternalsT.OnChangeFunc = useCallback(
    (e) => {
      if (shouldPreventValueChanges) return;
      let newMonth = e?.target?.value;
      // converts months 2~9 to 02~09
      if (Number.parseInt(newMonth, 10) > 1) newMonth = prependStringWithPlaceHolders(newMonth, 2);
      handleChangeMonth(newMonth, e);
      // auto-advance functionality:
      if (newMonth.length === 2) trackFocusDayInput();
    },
    [handleChangeMonth, shouldPreventValueChanges, trackFocusDayInput],
  );
  const onMonthKeyDown: DSControlledDateTimePickerInternalsT.OnKeyDown = useCallback(
    (e) => {
      const { key, ctrlKey, metaKey } = e;
      const monthStringConvertedToInteger = Number.parseInt(month, 10);
      /* ***** CODE TO HANDLE FOCUS CHANGES ***** */
      if (['Home', 'End', 'ArrowUp', 'ArrowDown', '-', '/', '\\'].includes(key)) e.preventDefault();
      if (['Home', 'End', 'ArrowUp', 'ArrowDown', '-', '/', '\\'].includes(key)) e.stopPropagation();
      if (key === 'Home') onHomeKeyDown();
      if (key === 'End') onEndKeyDown();
      // focus last fragment on fill with now
      if ((ctrlKey || metaKey) && key === ';') trackFocusLastSegment();

      if (shouldPreventValueChanges) return;

      /* ***** CODE TO HANDLE VALUE CHANGES ***** */
      if (key === 'ArrowUp' || key === 'ArrowDown') {
        const monthInteger = Number.isNaN(monthStringConvertedToInteger) ? 0 : monthStringConvertedToInteger;
        let newMonth;
        if (key === 'ArrowUp') newMonth = monthInteger + 1 <= 12 ? monthInteger + 1 : monthInteger;
        else newMonth = monthInteger - 1 > 0 ? monthInteger - 1 : 1;
        newMonth = prependStringWithPlaceHolders(`${newMonth}`, 2);
        handleChangeMonth(newMonth, e);
      }
      if (!Number.isNaN(monthStringConvertedToInteger) && (key === '-' || key === '/' || key === '\\')) {
        let newMonth = prependStringWithPlaceHolders(`${monthStringConvertedToInteger}`, 2);
        // occam's razor: if month is 00, convert to 01 and move on
        if (newMonth === '00') newMonth = '01';
        handleChangeMonth(newMonth, e);
        // auto-advance functionality:
        trackFocusDayInput();
      }
    },
    [
      month,
      onHomeKeyDown,
      onEndKeyDown,
      trackFocusLastSegment,
      shouldPreventValueChanges,
      handleChangeMonth,
      trackFocusDayInput,
    ],
  );
  const onMonthBlur: DSControlledDateTimePickerInternalsT.OnBlurFunc = useCallback(
    (e) => {
      const newMonth = e?.target?.value;
      if (newMonth === '0') handleChangeMonth('01', e, { isAutomaticFillTrigger: true });
      else if (newMonth.length > 0 && newMonth.length < 2)
        handleChangeMonth(prependStringWithPlaceHolders(newMonth, 2), e, {
          isAutomaticFillTrigger: true,
        });
    },
    [handleChangeMonth],
  );

  const handleChangeDay = useCallback(
    (
      newDay: string,
      e: DSControlledDateTimePickerT.OnInternalValuesChangeEvent,
      metaInfo: Partial<DSControlledDateTimePickerT.InternalInputsChangeMetaInfo> = defaultMetaInfo,
    ) => {
      if (shouldPreventValueChanges) return;
      const newDateString = getFormattedDateString({
        month,
        day: newDay,
        year,
      });
      const finalMetaInfo: DSControlledDateTimePickerT.InternalInputsChangeMetaInfo = {
        ...metaInfo,
        ...getValidationDateStringMetaInfo(newDateString, getIsOutOfRangeDay, getIsDisabledDay),
        newDateString,
      };
      appOnDayChange(newDay, e, finalMetaInfo);
      handleChangeComposedDateString(newDateString, finalMetaInfo);
    },
    [
      shouldPreventValueChanges,
      month,
      year,
      getIsOutOfRangeDay,
      getIsDisabledDay,
      appOnDayChange,
      handleChangeComposedDateString,
    ],
  );
  const onDayFocus: DSControlledDateTimePickerInternalsT.OnFocusFunc = useCallback(() => {
    trackFocusDayInput();
  }, [trackFocusDayInput]);
  const onDayChange: DSControlledDateTimePickerInternalsT.OnChangeFunc = useCallback(
    (e) => {
      if (shouldPreventValueChanges) return;
      let newDay = e?.target?.value;
      // converts days 4~9 to 04~09
      if (Number.parseInt(newDay, 10) > 3) newDay = prependStringWithPlaceHolders(newDay, 2);
      handleChangeDay(newDay, e);
      // auto-advance functionality:
      if (newDay.length === 2) trackFocusYearInput();
    },
    [handleChangeDay, shouldPreventValueChanges, trackFocusYearInput],
  );
  const onDayKeyDown: DSControlledDateTimePickerInternalsT.OnKeyDown = useCallback(
    (e) => {
      const { key, ctrlKey, metaKey, target, currentTarget } = e;
      const dayStringConvertedToInteger = Number.parseInt(day, 10);
      if (['Home', 'End', 'ArrowUp', 'ArrowDown', '-', '/', '\\'].includes(key)) e.preventDefault();
      if (['Home', 'End', 'ArrowUp', 'ArrowDown', '-', '/', '\\'].includes(key)) e.stopPropagation();

      /* ***** CODE TO HANDLE FOCUS CHANGES ***** */
      if (key === 'Home') onHomeKeyDown();
      if (key === 'End') onEndKeyDown();
      // focus last fragment on fill with now
      if ((ctrlKey || metaKey) && key === ';') trackFocusLastSegment();

      if (shouldPreventValueChanges) return;

      /* ***** CODE TO HANDLE VALUE CHANGES ***** */
      if (key === 'ArrowUp' || key === 'ArrowDown') {
        const dayInteger = Number.isNaN(dayStringConvertedToInteger) ? 0 : dayStringConvertedToInteger;
        let newDay;
        if (key === 'ArrowUp') newDay = dayInteger + 1 <= 31 ? dayInteger + 1 : dayInteger;
        else newDay = dayInteger - 1 > 0 ? dayInteger - 1 : 1;
        newDay = prependStringWithPlaceHolders(`${newDay}`, 2);
        handleChangeDay(newDay, e);
      }
      if (!Number.isNaN(dayStringConvertedToInteger) && (key === '-' || key === '/' || key === '\\')) {
        let newDay = prependStringWithPlaceHolders(`${dayStringConvertedToInteger}`, 2);
        // occam's razor: if day is 00, convert to 01 and move on
        if (newDay === '00') newDay = '01';
        handleChangeDay(newDay, e);
        // auto-advance functionality:
        trackFocusYearInput();
      }
      // regressive backspace functionality
      if (
        key === 'Backspace' &&
        target === currentTarget &&
        (target as HTMLInputElement).selectionStart === 0 &&
        (target as HTMLInputElement).selectionEnd === 0
      ) {
        e.preventDefault();
        trackFocusMonthInput();
      }
    },
    [
      day,
      onHomeKeyDown,
      onEndKeyDown,
      trackFocusLastSegment,
      shouldPreventValueChanges,
      handleChangeDay,
      trackFocusYearInput,
      trackFocusMonthInput,
    ],
  );
  const onDayBlur: DSControlledDateTimePickerInternalsT.OnBlurFunc = useCallback(
    (e) => {
      const newDay = e?.target?.value;
      if (newDay === '0') handleChangeDay('01', e, { isAutomaticFillTrigger: true });
      else if (newDay.length > 0 && newDay.length < 2)
        handleChangeDay(prependStringWithPlaceHolders(newDay, 2), e, {
          isAutomaticFillTrigger: true,
        });
    },
    [handleChangeDay],
  );

  const handleChangeYear = useCallback(
    (
      newYear: string,
      e: DSControlledDateTimePickerT.OnInternalValuesChangeEvent,
      metaInfo: Partial<DSControlledDateTimePickerT.InternalInputsChangeMetaInfo> = defaultMetaInfo,
    ) => {
      if (shouldPreventValueChanges) return;
      const newDateString = getFormattedDateString({
        month,
        day,
        year: newYear,
      });
      const finalMetaInfo: DSControlledDateTimePickerT.InternalInputsChangeMetaInfo = {
        ...metaInfo,
        ...getValidationDateStringMetaInfo(newDateString, getIsOutOfRangeDay, getIsDisabledDay),
        newDateString,
      };
      appOnYearChange(newYear, e, finalMetaInfo);
      handleChangeComposedDateString(newDateString, finalMetaInfo);
    },
    [
      shouldPreventValueChanges,
      month,
      day,
      getIsOutOfRangeDay,
      getIsDisabledDay,
      appOnYearChange,
      handleChangeComposedDateString,
    ],
  );
  const onYearFocus: DSControlledDateTimePickerInternalsT.OnFocusFunc = useCallback(() => {
    trackFocusYearInput();
  }, [trackFocusYearInput]);
  const onYearChange: DSControlledDateTimePickerInternalsT.OnChangeFunc = useCallback(
    (e) => {
      if (shouldPreventValueChanges) return;
      const newYear = e?.target?.value;
      handleChangeYear(newYear, e);
      // auto-advance functionality:
      if (newYear.length === 4) {
        if (!hideTime) trackFocusHourInput();
        else if (withAnyRightController) {
          if (withClearBtn) trackFocusClearBtn();
          else if (withAnyPicker) trackFocusPicker();
          // pending approval from Seda, check which one should be focused first...
        }
      }
    },
    [
      shouldPreventValueChanges,
      handleChangeYear,
      hideTime,
      trackFocusHourInput,
      withAnyRightController,
      withClearBtn,
      trackFocusClearBtn,
      withAnyPicker,
      trackFocusPicker,
    ],
  );
  const onYearKeyDown: DSControlledDateTimePickerInternalsT.OnKeyDown = useCallback(
    (e) => {
      const { key, ctrlKey, metaKey, target, currentTarget } = e;

      /* ****** New year value calculator logic ********* */
      const yearStringConvertedToInteger = Number.parseInt(year, 10);
      const yearInteger = Number.isNaN(yearStringConvertedToInteger) ? 0 : yearStringConvertedToInteger;
      let newYear = yearInteger !== 0 ? fillStringWithPlaceHolders(`${yearInteger}`, 4, '0') : '';
      if (key === 'ArrowUp' || key === 'ArrowDown') {
        let newYearInteger;
        if (key === 'ArrowUp') newYearInteger = yearInteger + 1 <= 9999 ? yearInteger + 1 : yearInteger;
        else newYearInteger = yearInteger - 1 > 0 ? yearInteger - 1 : 1;
        newYear = prependStringWithPlaceHolders(`${newYearInteger}`, 4);
      }
      const isAutomaticFillTrigger = newYear !== year && ['Tab', ' '].includes(key);
      const metainfo = { isAutomaticFillTrigger };

      /* ***** CODE TO HANDLE FOCUS CHANGES ***** */
      /* ************* focus tracking logic ************* */
      let trackNextFocus = trackFocusResetter; // if nothing after, we reset internal focus tracker to avoid re-render unexpected focus shift
      if (!hideTime) trackNextFocus = trackFocusHourInput;
      else if (withClearBtn) trackNextFocus = trackFocusClearBtn;
      else if (withAnyPicker) trackNextFocus = trackFocusPicker;

      if (['Home', 'End', 'ArrowUp', 'ArrowDown', ' '].includes(key)) e.preventDefault();
      if (['Home', 'End', 'ArrowUp', 'ArrowDown', ' '].includes(key)) e.stopPropagation();
      if (key === 'Home') onHomeKeyDown();
      if (key === 'End') onEndKeyDown(); // focus last fragment on fill with now
      if ((ctrlKey || metaKey) && key === ';') trackFocusLastSegment();

      if (shouldPreventValueChanges) return;

      /* ***** CODE TO HANDLE VALUE CHANGES ***** */
      /* ************* new year apply logic ************* */
      // tab/spacebar applies auto-fill
      // arrows up/down applies increment/decrement
      if (key === 'ArrowUp' || key === 'ArrowDown' || isAutomaticFillTrigger) {
        handleChangeYear(newYear, e, metainfo);
      }

      /* ************** auto-advance logic ************** */
      if (['Tab', ' '].includes(key)) {
        // occam's razor: if user typed 0 and pressed space/tab, convert to 0000 and move on
        if (year !== '' && yearInteger === 0) handleChangeYear('0000', e);
        // auto-advance functionality:
        if (key === ' ') trackNextFocus(); // tab must be handled by the browser
      }
      /* ********** regressive backspace logic ********** */
      if (
        key === 'Backspace' &&
        target === currentTarget &&
        (target as HTMLInputElement).selectionStart === 0 &&
        (target as HTMLInputElement).selectionEnd === 0
      ) {
        e.preventDefault();
        trackFocusDayInput();
      }
    },
    [
      year,
      trackFocusResetter,
      hideTime,
      trackFocusHourInput,
      withClearBtn,
      trackFocusClearBtn,
      withAnyPicker,
      trackFocusPicker,
      onHomeKeyDown,
      onEndKeyDown,
      trackFocusLastSegment,
      shouldPreventValueChanges,
      handleChangeYear,
      trackFocusDayInput,
    ],
  );
  const onYearBlur: DSControlledDateTimePickerInternalsT.OnBlurFunc = useCallback(
    (e) => {
      const newYear = e?.target?.value;
      if (newYear === '0') handleChangeYear('0000', e, { isAutomaticFillTrigger: true });
      else if (newYear.length > 0 && newYear.length < 4)
        handleChangeYear(fillStringWithPlaceHolders(newYear, 4, '0'), e, {
          isAutomaticFillTrigger: true,
        });
    },
    [handleChangeYear],
  );

  return useMemo(
    () => ({
      month,
      day,
      year,
      autoFocusMonthInput,
      innerRefMonthInput,
      disabled,
      applyAriaDisabled,
      onMonthFocus,
      onMonthChange,
      onDayFocus,
      onDayChange,
      onYearFocus,
      onYearChange,
      onMonthBlur,
      onDayBlur,
      onYearBlur,
      onMonthKeyDown,
      onDayKeyDown,
      onYearKeyDown,
    }),
    [
      month,
      day,
      year,
      autoFocusMonthInput,
      innerRefMonthInput,
      disabled,
      applyAriaDisabled,
      onMonthFocus,
      onMonthChange,
      onDayFocus,
      onDayChange,
      onYearFocus,
      onYearChange,
      onMonthBlur,
      onDayBlur,
      onYearBlur,
      onMonthKeyDown,
      onDayKeyDown,
      onYearKeyDown,
    ],
  );
};
